import React, { useRef, useEffect } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { stripHtml } from "string-strip-html";
import { getPlainTextLength } from "../helpers/editorFunc";

const Editor = ({ value, setValue, placeholder = "", maxLength, onLimit }) => {
    const quillRef = useRef(null);
    const lastAcceptedDeltaRef = useRef(null);

    const Bold = Quill.import("formats/bold");
    const Ital = Quill.import("formats/italic");
    Bold.tagName = "B";
    Ital.tagName = "I";
    Quill.register(Bold, true);
    Quill.register(Ital, true);

    const normalizeText = (text) => {
        return text
            .replace(/[«»]/g, '"')
            .replace(/[""]/g, '"')
            .replace(/['']/g, "'")
            .replace(/—/g, "-")
            .replace(/–/g, "-")
            .replace(/\u00A0/g, " ")
            .replace(/…/g, "...")
            .replace(/[\u2018\u2019]/g, "'")
            .replace(/[\u201C\u201D]/g, '"');
    };

    useEffect(() => {
        if (quillRef.current) {
            const quill = quillRef.current.getEditor();
            lastAcceptedDeltaRef.current = quill.getContents();

            const handlePaste = (e) => {
                e.preventDefault();
                const text = e.clipboardData?.getData("text/plain");

                if (text) {
                    const normalized = normalizeText(text);
                    const selection = quill.getSelection(true);

                    if (selection) {
                        quill.deleteText(selection.index, selection.length);
                        quill.insertText(selection.index, normalized);
                        quill.setSelection(selection.index + normalized.length);
                    }
                }
            };

            quill.root.addEventListener("paste", handlePaste);

            return () => {
                quill.root.removeEventListener("paste", handlePaste);
            };
        }
    }, []);

    let modules = {
        clipboard: {
            matchVisual: false,
        },
        toolbar: [
            ["bold", "italic", "underline", "strike"],
            ["link"],
            ["clean"],
        ],
    };

    let formats = [
        "bold",
        "italic",
        "underline",
        "strike",
        "blockquote",
        "link",
    ];

    const isEmptyContent = (content) => {
        return stripHtml(content).result.trim().length === 0;
    };

    const handleChange = (value, delta, source, editor) => {
        const plainTextLength = getPlainTextLength(value);
        if (maxLength && plainTextLength > maxLength) {
            if (quillRef.current && lastAcceptedDeltaRef.current) {
                quillRef.current
                    .getEditor()
                    .setContents(lastAcceptedDeltaRef.current, "silent");
            }
            if (onLimit) onLimit(maxLength, plainTextLength);
            return;
        }

        const newValue = isEmptyContent(value) ? "" : value;
        lastAcceptedDeltaRef.current = editor.getContents();
        setValue(newValue, delta, source, editor);
    };

    return (
        <div style={{ width: "100%", marginBottom: "12px" }}>
            <ReactQuill
                ref={quillRef}
                theme="snow"
                value={value}
                onChange={handleChange}
                placeholder={placeholder}
                modules={modules}
                formats={formats}
            />
        </div>
    );
};

export default Editor;
